<div class="list-toolbar">
  <mat-form-field appearance="outline" class="filter" subscriptSizing="dynamic">
    <mat-label>Payment</mat-label>
    <mat-select [(ngModel)]="paymentStatus" (selectionChange)="onFilterChange()">
      <mat-option value="">All</mat-option>
      <mat-option value="NotPayed">Unpaid</mat-option>
      <mat-option value="Payed">Paid</mat-option>
    </mat-select>
  </mat-form-field>
  <mat-checkbox [(ngModel)]="includeZeroCoins" (change)="onFilterChange()">Show 0-coin contracts</mat-checkbox>
</div>

<p class="hint">
  <mat-icon>info</mat-icon>
  @if (!state.supplierId) { Filter by a supplier to select all of their unpaid contracts at once. }
  Active contracts appear here once they finish. The date range applies to transfer runs only.
</p>

@if (canSelectAll && pageAllSelected) {
<div class="select-banner">
  @if (allUnpaidCount !== null) {
  <span>All <b>{{ allUnpaidCount }}</b> unpaid contracts of this supplier are selected.</span>
  <button mat-button type="button" (click)="clearSelection()">Clear selection</button>
  } @else {
  <span><b>{{ selectedOnPage }}</b> unpaid contracts on this page are selected.</span>
  @if (canSelectAllPages) {
  <button mat-button type="button" [disabled]="selectingAll" (click)="selectAllUnpaid()">
    Select all unpaid contracts of this supplier
    @if (selectingAll) { <mat-spinner class="spinner-inline" diameter="16"></mat-spinner> }
  </button>
  }
  }
</div>
}

<div class="table-card">
@if (loading) {
<div class="table-state"><mat-spinner diameter="28"></mat-spinner></div>
} @else if (error) {
<div class="table-state error">{{ error }}</div>
} @else if (contracts.length === 0) {
<div class="table-state">No finished manual contracts match these filters.</div>
} @else {

@if (isMobile$ | async) {
@if (canSelectAll) {
<label class="select-all-row">
  <mat-checkbox [checked]="pageAllSelected" [indeterminate]="pageSomeSelected" (change)="togglePage()"></mat-checkbox>
  Select all unpaid on this page
</label>
}
<div class="cards">
  @for (c of contracts; track c.id) {
  <div class="card" [class.card-selected]="state.contracts.has(c.id)">
    <div class="card-head">
      <mat-checkbox [disabled]="c.payment_status === 'Payed'" [checked]="state.contracts.has(c.id)"
        (change)="toggle(c)"></mat-checkbox>
      <div class="card-title">
        {{ label(c) }}
        <p class="card-meta">
          <span class="supplier-link" (click)="openSupplierInfoDialog(c.supplier_id, c.supplier_name)">{{ c.supplier_name }}</span>
        </p>
        <p class="card-meta">{{ c.created_at | date:'medium' }} · {{ c.status }}</p>
      </div>
      <span class="chip" [ngClass]="paymentClass(c.payment_status)">{{ paymentLabel(c.payment_status) }}</span>
    </div>
    <div class="card-stats">
      <div class="stat"><span class="label">Coins</span><span class="value">{{ c.coins | number }}</span></div>
      <div class="stat"><span class="label">Fee</span><span class="value">{{ c.fee | number }}</span></div>
      <div class="stat"><span class="label">Payable</span><span class="value">{{ c.payable_amount | number }}</span></div>
    </div>
    @if (canEdit) {
    <div class="card-actions">
      @if (c.payment_status !== 'Payed') {
      <button mat-button type="button" (click)="openEditFeeDialog(c)"><mat-icon>edit</mat-icon> Edit fee</button>
      }
      <button mat-button type="button" (click)="togglePaymentStatus(c)">
        <mat-icon>sync_alt</mat-icon> Mark {{ c.payment_status === 'Payed' ? 'unpaid' : 'paid' }}
      </button>
    </div>
    }
  </div>
  }
</div>
} @else {
<div class="table-scroll">
  <table mat-table [dataSource]="contracts">
    <ng-container matColumnDef="select">
      <th mat-header-cell *matHeaderCellDef>
        @if (canSelectAll) {
        <mat-checkbox [checked]="pageAllSelected" [indeterminate]="pageSomeSelected" (change)="togglePage()"
          matTooltip="Select all unpaid contracts on this page"></mat-checkbox>
        }
      </th>
      <td mat-cell *matCellDef="let c">
        <mat-checkbox [disabled]="c.payment_status === 'Payed'" [checked]="state.contracts.has(c.id)"
          (change)="toggle(c)"></mat-checkbox>
      </td>
    </ng-container>
    <ng-container matColumnDef="date">
      <th mat-header-cell *matHeaderCellDef>Created</th>
      <td mat-cell *matCellDef="let c">
        {{ c.created_at | date:'short' }}
        <div class="sub">{{ c.status }}</div>
      </td>
    </ng-container>
    <ng-container matColumnDef="supplier">
      <th mat-header-cell *matHeaderCellDef>Supplier</th>
      <td mat-cell *matCellDef="let c">
        <span class="supplier-link" matTooltip="View buyer info"
          (click)="openSupplierInfoDialog(c.supplier_id, c.supplier_name)">{{ c.supplier_name }}</span>
      </td>
    </ng-container>
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef>Contract</th>
      <td mat-cell *matCellDef="let c">{{ label(c) }}</td>
    </ng-container>
    <ng-container matColumnDef="coins">
      <th mat-header-cell *matHeaderCellDef matTooltip="Received coins from this contract's bought cards">Coins</th>
      <td mat-cell *matCellDef="let c" class="num">{{ c.coins | number }}</td>
    </ng-container>
    <ng-container matColumnDef="fee">
      <th mat-header-cell *matHeaderCellDef>Fee</th>
      <td mat-cell *matCellDef="let c" class="num">
        <span class="inline-actions">
          {{ c.fee | number }}
          @if (canEdit && c.payment_status !== 'Payed') {
          <button mat-icon-button class="mini-btn" matTooltip="Edit fee rate" (click)="openEditFeeDialog(c)">
            <mat-icon>edit</mat-icon>
          </button>
          }
        </span>
      </td>
    </ng-container>
    <ng-container matColumnDef="payable">
      <th mat-header-cell *matHeaderCellDef>Payable</th>
      <td mat-cell *matCellDef="let c" class="num strong">{{ c.payable_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="payment">
      <th mat-header-cell *matHeaderCellDef>Payment</th>
      <td mat-cell *matCellDef="let c">
        <span class="inline-actions">
          <span class="chip" [ngClass]="paymentClass(c.payment_status)">{{ paymentLabel(c.payment_status) }}</span>
          @if (canEdit) {
          <button mat-icon-button class="mini-btn" matTooltip="Mark {{ c.payment_status === 'Payed' ? 'unpaid' : 'paid' }}"
            (click)="togglePaymentStatus(c)">
            <mat-icon>sync_alt</mat-icon>
          </button>
          }
        </span>
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="columns"></tr>
    <tr mat-row *matRowDef="let row; columns: columns;" [class.row-selected]="state.contracts.has(row.id)"></tr>
  </table>
</div>
}

<mat-paginator
  [length]="total"
  [pageIndex]="pageIndex"
  [pageSize]="pageSize"
  [pageSizeOptions]="pageSizeOptions"
  [showFirstLastButtons]="true"
  (page)="onPage($event)">
</mat-paginator>
}
</div>
